import { useMemo, useState } from "react";
import { ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceArea } from "recharts";
import { ChartTooltip } from "./ChartTooltip";
import { MetricToggle } from "./MetricToggle";
import { monthLabel } from "../lib/csvAnalysis";
import { fmtDur, pctChange } from "../lib/pickerSheet";
import { fmtNum, fmtCompact } from "../lib/format";

const avgPeriod = (rows) => {
  const t = rows.reduce((a, r) => { ["visits", "qty", "worked", "lost", "days", "absent", "active"].forEach((k) => { a[k] += r[k] || 0; }); return a; }, { visits: 0, qty: 0, worked: 0, lost: 0, days: 0, absent: 0, active: 0 });
  const n = rows.length || 1, h = t.worked / 3600;
  return { visits: t.visits / n, qty: t.qty / n, hrs: h / n, vph: h ? t.visits / h : 0, qph: h ? t.qty / h : 0, active: t.active / n, perPicker: t.active ? t.visits / t.active : 0, lostDay: t.days ? t.lost / t.days : 0, absent: t.absent / n };
};

const int = (v) => fmtNum(Math.round(v));

const METRICS = [
  ["visits", "Visits / month", int, false], ["qty", "Qty picked / month", int, false], ["hrs", "Hours worked / month", int, null],
  ["active", "Active pickers", (v) => v.toFixed(1), null], ["perPicker", "Visits per picker / month", int, false],
  ["vph", "Visits / hr", fmtNum, false], ["qph", "Qty / hr", fmtNum, false], ["lostDay", "Lost / picker-day", fmtDur, true], ["absent", "Absent days / month", fmtNum, true],
];

const range = (rows) => (rows.length ? `${monthLabel(rows[0].key)} – ${monthLabel(rows[rows.length - 1].key)}` : "—");

export const HistoryCompare = ({ team }) => {
  const last12 = team.slice(-12);
  const janIdx = last12.findIndex((r) => r.key.endsWith("-01"));
  const opts = [["first3", "First 3 months"], ...(janIdx >= 0 && janIdx + 3 <= last12.length - 3 ? [["jan", "Jan–Mar"]] : [])];
  const [base, setBase] = useState("first3");
  const startRows = base === "jan" ? last12.slice(janIdx, janIdx + 3) : last12.slice(0, 3);
  const nowRows = last12.slice(-3);
  const a = useMemo(() => avgPeriod(startRows), [startRows]);
  const b = useMemo(() => avgPeriod(nowRows), [nowRows]);
  if (last12.length < 6) return null;
  const ch = (k) => pctChange(b[k], a[k]);
  const sign = (v) => (v >= 0 ? "up" : "down");
  const story = [
    `Visits averaged ${fmtNum(Math.round(a.visits))} a month in ${range(startRows)}, compared with ${fmtNum(Math.round(b.visits))} in ${range(nowRows)}: ${sign(ch("visits"))} ${Math.abs(ch("visits")).toFixed(1)}% (${b.visits >= a.visits ? "+" : "−"}${fmtNum(Math.round(Math.abs(b.visits - a.visits)))} visits a month).`,
    `Quantity picked moved from ${fmtNum(Math.round(a.qty))} to ${fmtNum(Math.round(b.qty))} a month: ${sign(ch("qty"))} ${Math.abs(ch("qty")).toFixed(1)}% (${b.qty >= a.qty ? "+" : "−"}${fmtNum(Math.round(Math.abs(b.qty - a.qty)))} units a month).`,
    `Productivity: visits per hour ${sign(ch("vph"))} ${Math.abs(ch("vph")).toFixed(1)}% (${fmtNum(a.vph)} → ${fmtNum(b.vph)}); quantity per hour ${sign(ch("qph"))} ${Math.abs(ch("qph")).toFixed(1)}% (${fmtNum(a.qph)} → ${fmtNum(b.qph)}).`,
    `Headcount averaged ${a.active.toFixed(1)} active pickers then vs ${b.active.toFixed(1)} now, and each picker now does ${fmtNum(Math.round(b.perPicker))} visits a month vs ${fmtNum(Math.round(a.perPicker))} (${sign(ch("perPicker"))} ${Math.abs(ch("perPicker")).toFixed(1)}%).`,
    `Lost time per picker-day went from ${fmtDur(a.lostDay)} to ${fmtDur(b.lostDay)}; absent days from ${fmtNum(a.absent)} to ${fmtNum(b.absent)} a month.`,
  ];
  const chart = last12.map((r) => ({ label: monthLabel(r.key), visits: r.visits, qty: Math.round(r.qty) }));
  return (
    <div className="panel p-5 sm:p-6 space-y-6" data-testid="history-compare">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="label-mono">12-month history · start vs now</p>
          <h3 className="font-display text-xl font-semibold text-slate-100 mt-1" data-testid="history-title">{range(startRows)} vs {range(nowRows)}</h3>
          <p className="text-xs text-slate-500">Monthly averages over each 3-month window, last 12 full months</p>
        </div>
        {opts.length > 1 && <div className="no-print"><MetricToggle value={base} onChange={setBase} options={opts} prefix="history-base" /></div>}
      </div>

      <ul className="space-y-2" data-testid="history-story">{story.map((t, i) => <li key={i} className="text-sm text-slate-200 flex gap-3"><span className="text-ep-soft font-mono">{String(i + 1).padStart(2, "0")}</span>{t}</li>)}</ul>

      <div style={{ height: 280 }} data-pack="hide">
        <ResponsiveContainer width="100%" height={280} initialDimension={{ width: 900, height: 280 }}>
          <ComposedChart data={chart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#1F2937" vertical={false} />
            <XAxis dataKey="label" stroke="#64748B" tick={{ fontSize: 11 }} tickLine={false} />
            <YAxis yAxisId="v" stroke="#D4333B" tickFormatter={fmtCompact} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={44} />
            <YAxis yAxisId="q" orientation="right" stroke="#10B981" tickFormatter={fmtCompact} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={44} />
            <ReferenceArea yAxisId="v" x1={monthLabel(startRows[0].key)} x2={monthLabel(startRows[startRows.length - 1].key)} fill="#64748B" fillOpacity={0.12} label={{ value: "Start", fill: "#94A3B8", fontSize: 11, position: "insideTop" }} />
            <ReferenceArea yAxisId="v" x1={monthLabel(nowRows[0].key)} x2={monthLabel(nowRows[nowRows.length - 1].key)} fill="#D4333B" fillOpacity={0.1} label={{ value: "Now", fill: "#F0646A", fontSize: 11, position: "insideTop" }} />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line yAxisId="v" dataKey="visits" name="Visits" stroke="#D4333B" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line yAxisId="q" dataKey="qty" name="Qty picked" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="history-table">
          <thead><tr className="border-b border-slate-800">{["Measure", `Start · ${range(startRows)}`, `Now · ${range(nowRows)}`, "Change", "Change %"].map((h) => <th key={h} className="text-left px-3 py-2.5 font-mono text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap first:pl-0">{h}</th>)}</tr></thead>
          <tbody>{METRICS.map(([k, l, f, inv]) => {
            const p = ch(k), diff = b[k] - a[k], good = p == null || inv === null ? null : inv ? p <= 0 : p >= 0;
            return (
              <tr key={k} className="border-b border-slate-800/50" data-testid={`history-row-${k}`}>
                <td className="py-2 text-slate-300">{l}</td>
                <td className="px-3 font-mono text-slate-400">{f(a[k])}</td>
                <td className="px-3 font-mono text-slate-100">{f(b[k])}</td>
                <td className="px-3 font-mono text-slate-300">{diff >= 0 ? "+" : "−"}{k === "lostDay" ? fmtDur(Math.abs(diff)) : f(Math.abs(diff))}</td>
                <td className={`px-3 font-mono ${good == null ? "text-slate-300" : good ? "text-emerald-400" : "text-rose-400"}`}>{p == null ? "—" : `${p >= 0 ? "▲" : "▼"} ${Math.abs(p).toFixed(1)}%`}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    </div>
  );
};
